import type { SourceFilterOption } from "@ui/molecules/SourceFilter";

export type PostSourceFields = {
	account_id?: string | null;
	handle?: string | null;
	platform?: string | null;
};

export function isDirectUploadPlatform(platform: string | null | undefined): boolean {
	return platform?.trim().toLowerCase().replace(/\s+/g, "_") === "direct_upload";
}

export function sourceFilterKey(post: PostSourceFields): string {
	if (post.account_id) {
		return post.account_id;
	}
	if (isDirectUploadPlatform(post.platform)) {
		return "direct-upload";
	}
	if (post.handle) {
		return `handle:${post.handle}`;
	}
	return "unknown";
}

export function sourceFilterLabel(post: PostSourceFields): string {
	if (isDirectUploadPlatform(post.platform)) {
		return "Uploaded";
	}
	if (post.handle) {
		return `@${post.handle}`;
	}
	return "Unknown source";
}

export function sourceFilterOptionFromPost(
	post: PostSourceFields,
): SourceFilterOption {
	const directUpload = isDirectUploadPlatform(post.platform);
	return {
		value: sourceFilterKey(post),
		label: sourceFilterLabel(post),
		platform: directUpload ? undefined : post.platform ?? undefined,
		symbol: directUpload ? "upload" : undefined,
	};
}

export function sourceFilterOptionsFromPosts(
	posts: readonly PostSourceFields[],
): SourceFilterOption[] {
	const byKey = new Map<string, SourceFilterOption>();
	for (const post of posts) {
		const option = sourceFilterOptionFromPost(post);
		if (option.value && !byKey.has(option.value)) {
			byKey.set(option.value, option);
		}
	}
	return [...byKey.values()].sort((a, b) => a.label.localeCompare(b.label));
}

export function connectedAccountSourceOption(account: {
	id?: string | null;
	name?: string | null;
	handle?: string | null;
	platform?: string | null;
}): SourceFilterOption | null {
	const value = account.id?.trim() ?? "";
	if (value === "") {
		return null;
	}
	if (isDirectUploadPlatform(account.platform)) {
		return { value, label: "Uploaded", symbol: "upload" };
	}
	const handle = account.handle?.trim();
	const label = handle
		? `@${handle}`
		: (account.name?.trim() || value);
	return {
		value,
		label,
		platform: account.platform ?? undefined,
	};
}

export function mergeSourceFilterOptions(
	...groups: readonly (readonly SourceFilterOption[])[]
): SourceFilterOption[] {
	const byKey = new Map<string, SourceFilterOption>();
	for (const group of groups) {
		for (const option of group) {
			if (option.value === "" || option.value === "all") {
				continue;
			}
			if (!byKey.has(String(option.value))) {
				byKey.set(String(option.value), option);
			}
		}
	}
	return [...byKey.values()].sort((a, b) => a.label.localeCompare(b.label));
}
